import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { User, Loader2, Pencil, Trash2, MapPin, Mail, Eye,
         Phone as PhoneIcon, Inbox, Plus, Star, Send, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { meApi, listingsApi, toAbs } from '../api/client';
import ListingForm from '../components/ListingForm';

const Tab = ({ active, onClick, children, badge }) => (
  <button onClick={onClick}
    className={`px-4 py-2.5 text-sm rounded-md transition-colors inline-flex items-center gap-2 ${
      active ? 'bg-[#a83a5b] text-white' : 'text-[#b8a9a3] hover:text-white hover:bg-[#241a1f]'
    }`}>
    {children}
    {badge > 0 && (
      <span className={`inline-flex items-center justify-center px-1.5 min-w-[20px] h-5 rounded-full text-[10px] font-semibold ${
        active ? 'bg-white/20 text-white' : 'bg-[#a83a5b] text-white'
      }`}>{badge}</span>
    )}
  </button>
);

const ProfilePage = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get('tab') || 'listings');
  const [listings, setListings] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null); // {thread_id, listing_name,...}
  const [threadMessages, setThreadMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const chatScrollRef = useRef(null);

  const scrollChatBottom = () => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    if (!authLoading && !user) navigate('/inserate');
  }, [user, authLoading, navigate]);

  useEffect(() => { setSp((prev) => { const n = new URLSearchParams(prev); n.set('tab', tab); return n; }, { replace: true }); }, [tab]); // eslint-disable-line

  const refresh = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [l, t] = await Promise.all([meApi.listings(), meApi.threads()]);
      setListings(l); setThreads(t);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) refresh(); }, [user]);

  const unreadCount = threads.reduce((s, t) => s + (t.unread_count || 0), 0);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Inserat "${name}" wirklich löschen? Dies kann nicht rückgängig gemacht werden.`)) return;
    try {
      await listingsApi.remove(id);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const openThread = async (t) => {
    setActiveThread(t);
    setLoadingThread(true);
    try {
      const msgs = await meApi.thread(t.thread_id);
      setThreadMessages(msgs);
      setThreads((prev) => prev.map((x) => x.thread_id === t.thread_id ? { ...x, unread_count: 0 } : x));
      setTimeout(scrollChatBottom, 50);
    } catch {} finally { setLoadingThread(false); }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    const body = replyText.trim();
    if (!body || !activeThread) return;
    setSending(true);
    try {
      const msg = await meApi.reply(activeThread.thread_id, body);
      setThreadMessages((prev) => [...prev, msg]);
      setReplyText('');
      // update thread summary last_body/last_at
      setThreads((prev) => prev.map((t) =>
        t.thread_id === activeThread.thread_id
          ? { ...t, last_body: msg.body, last_at: msg.created_at }
          : t));
      setTimeout(scrollChatBottom, 50);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Antwort fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  const deleteThread = async (threadId) => {
    if (!window.confirm('Gesamte Konversation löschen?')) return;
    try {
      await meApi.deleteThread(threadId);
      setThreads((prev) => prev.filter((t) => t.thread_id !== threadId));
      if (activeThread?.thread_id === threadId) {
        setActiveThread(null); setThreadMessages([]);
      }
    } catch {}
  };

  if (authLoading || !user) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#9a8a83]"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0f0a0c]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#a83a5b] to-[#5e1c30] flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-serif text-3xl text-white leading-tight">Mein Profil</h1>
              <p className="text-sm text-[#9a8a83]">{user.name} · {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setEditTarget(null); setShowForm(true); }}
              className="px-4 py-2.5 rounded-md bg-[#a83a5b] hover:bg-[#bf456b] text-white font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Neues Inserat
            </button>
            <button onClick={logout}
              className="px-4 py-2.5 rounded-md border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b]">
              Abmelden
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6 border-b border-[#2a1f24] pb-3 overflow-x-auto">
          <Tab active={tab === 'listings'} onClick={() => { setTab('listings'); setActiveThread(null); }} badge={0}>
            <Star className="w-4 h-4" /> Meine Inserate ({listings.length})
          </Tab>
          <Tab active={tab === 'messages'} onClick={() => setTab('messages')} badge={unreadCount}>
            <Inbox className="w-4 h-4" /> Nachrichten
          </Tab>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-[#9a8a83]"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : tab === 'listings' ? (
          listings.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-[#2a1f24] rounded-xl">
              <p className="text-[#9a8a83] mb-4">Du hast noch keine Inserate aufgegeben.</p>
              <button onClick={() => { setEditTarget(null); setShowForm(true); }}
                className="px-5 py-2.5 rounded-md bg-[#a83a5b] hover:bg-[#bf456b] text-white font-medium inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Erstes Inserat erstellen
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {listings.map((l) => {
                const [g1, g2] = l.gradient || ['#7a1f3d', '#c2185b'];
                const firstImg = (l.images && l.images[0]) || l.image_url;
                return (
                  <div key={l.id} className="bg-[#1a1417] border border-[#2a1f24] rounded-xl overflow-hidden flex">
                    <Link to={`/inserate/${l.slug}`} className="w-24 sm:w-32 shrink-0 relative"
                      style={{ background: `linear-gradient(135deg, ${g1}, ${g2})` }}>
                      {firstImg ? (
                        <img src={toAbs(firstImg)} alt={l.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center font-serif text-3xl text-white/40">{l.initials}</div>
                      )}
                    </Link>
                    <div className="flex-1 p-4 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <Link to={`/inserate/${l.slug}`}>
                          <h3 className="font-serif text-lg text-white hover:text-[#e8a3b8] truncate">{l.name}</h3>
                        </Link>
                        {l.top && (
                          <span className="shrink-0 text-[10px] uppercase tracking-wider text-[#f5d4be] inline-flex items-center gap-1">
                            <Star className="w-3 h-3 fill-[#f5d4be]" /> Top
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-[#9a8a83] flex-wrap mb-3">
                        <Link to={`/inserate?category=${encodeURIComponent(l.category)}`}
                          className="uppercase tracking-wider text-[#c19a8a] hover:text-white hover:underline">
                          {l.category}
                        </Link>
                        <Link to={`/inserate?region=${encodeURIComponent(l.region)}`}
                          className="inline-flex items-center gap-1 hover:text-white hover:underline">
                          <MapPin className="w-3 h-3" />{l.region}{l.city ? ` · ${l.city}` : ''}
                        </Link>
                      </div>
                      {l.service_tags && l.service_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {l.service_tags.slice(0, 5).map((t) => (
                            <span key={t} className="px-2 py-0.5 rounded-full bg-[#241a1f] border border-[#2a1f24] text-[10px] text-[#c19a8a]">{t}</span>
                          ))}
                          {l.service_tags.length > 5 && (
                            <span className="text-[10px] text-[#7a6b65] px-1">+{l.service_tags.length - 5}</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => { setEditTarget(l); setShowForm(true); }}
                          className="px-3 py-1.5 rounded-md text-xs border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b] inline-flex items-center gap-1.5">
                          <Pencil className="w-3.5 h-3.5" /> Bearbeiten
                        </button>
                        <button onClick={() => handleDelete(l.id, l.name)}
                          className="px-3 py-1.5 rounded-md text-xs border border-[#2a1f24] text-[#ff8aa3] hover:text-white hover:bg-[#5c1a1a] hover:border-[#5c1a1a] inline-flex items-center gap-1.5">
                          <Trash2 className="w-3.5 h-3.5" /> Löschen
                        </button>
                        <Link to={`/inserate/${l.slug}`}
                          className="px-3 py-1.5 rounded-md text-xs border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b] inline-flex items-center gap-1.5">
                          <Eye className="w-3.5 h-3.5" /> Ansehen
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          // Messages: Thread list + chat view
          threads.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-[#2a1f24] rounded-xl">
              <Inbox className="w-10 h-10 mx-auto mb-3 text-[#5a4d48]" />
              <p className="text-[#9a8a83]">Noch keine Nachrichten erhalten.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-[#1a1417] border border-[#2a1f24] rounded-xl overflow-hidden min-h-[500px]">
              {/* Thread list */}
              <div className={`${activeThread ? 'hidden md:block' : ''} md:col-span-1 border-r border-[#2a1f24] overflow-y-auto max-h-[70vh]`}>
                {threads.map((t) => (
                  <button key={t.thread_id} onClick={() => openThread(t)}
                    className={`w-full p-3 text-left border-b border-[#2a1f24] hover:bg-[#241a1f] transition-colors ${
                      activeThread?.thread_id === t.thread_id ? 'bg-[#241a1f]' : ''
                    }`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {t.unread_count > 0 && <span className="w-2 h-2 rounded-full bg-[#a83a5b] shrink-0" />}
                        <span className="font-medium text-white text-sm truncate">{t.from_name}</span>
                      </div>
                      <span className="text-[10px] text-[#7a6b65] whitespace-nowrap">
                        {new Date(t.last_at).toLocaleDateString('de-CH')}
                      </span>
                    </div>
                    <p className="text-xs text-[#c19a8a] truncate mb-1">{t.listing_name}</p>
                    <p className="text-xs text-[#9a8a83] line-clamp-2">{t.last_body}</p>
                    {t.unread_count > 0 && (
                      <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full bg-[#a83a5b] text-[9px] text-white font-semibold">
                        {t.unread_count} neu
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Chat view */}
              <div className={`${!activeThread ? 'hidden md:flex' : 'flex'} md:col-span-2 flex-col max-h-[70vh]`}>
                {!activeThread ? (
                  <div className="flex-1 flex items-center justify-center text-[#9a8a83] text-sm p-6">
                    Wähle eine Konversation aus
                  </div>
                ) : (
                  <>
                    {/* Chat header */}
                    <div className="flex items-center justify-between p-3 border-b border-[#2a1f24]">
                      <div className="flex items-center gap-2 min-w-0">
                        <button onClick={() => setActiveThread(null)}
                          className="md:hidden text-[#9a8a83] hover:text-white p-1">
                          <ArrowLeft className="w-4 h-4" />
                        </button>
                        <div className="min-w-0">
                          <p className="text-white font-medium text-sm truncate">{activeThread.from_name}</p>
                          <Link to={`/inserate/${activeThread.listing_slug}`}
                            className="text-xs text-[#c19a8a] hover:text-white truncate block">
                            → {activeThread.listing_name}
                          </Link>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {activeThread.from_email && (
                          <a href={`mailto:${activeThread.from_email}`} title={activeThread.from_email}
                            className="w-8 h-8 rounded-md hover:bg-[#241a1f] text-[#c19a8a] hover:text-white inline-flex items-center justify-center">
                            <Mail className="w-4 h-4" />
                          </a>
                        )}
                        {activeThread.from_phone && (
                          <a href={`tel:${activeThread.from_phone}`} title={activeThread.from_phone}
                            className="w-8 h-8 rounded-md hover:bg-[#241a1f] text-[#c19a8a] hover:text-white inline-flex items-center justify-center">
                            <PhoneIcon className="w-4 h-4" />
                          </a>
                        )}
                        <button onClick={() => deleteThread(activeThread.thread_id)} title="Konversation löschen"
                          className="w-8 h-8 rounded-md hover:bg-[#241a1f] text-[#ff8aa3] hover:text-white inline-flex items-center justify-center">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Messages */}
                    <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#15101280]">
                      {loadingThread ? (
                        <div className="flex items-center justify-center h-full text-[#9a8a83]"><Loader2 className="w-5 h-5 animate-spin" /></div>
                      ) : threadMessages.map((m) => {
                        const outgoing = m.direction === 'out';
                        return (
                          <div key={m.id} className={`flex ${outgoing ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl ${
                              outgoing
                                ? 'bg-[#a83a5b] text-white rounded-br-sm'
                                : 'bg-[#241a1f] border border-[#2a1f24] text-[#eae2df] rounded-bl-sm'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                              <p className={`text-[10px] mt-1 ${outgoing ? 'text-white/60' : 'text-[#7a6b65]'}`}>
                                {new Date(m.created_at).toLocaleString('de-CH', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Reply input */}
                    <form onSubmit={sendReply} className="p-3 border-t border-[#2a1f24] flex gap-2">
                      <input type="text" value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Antwort schreiben..."
                        className="flex-1 bg-[#241a1f] border border-[#2a1f24] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-[#5a4d48] focus:outline-none focus:border-[#a83a5b]" />
                      <button type="submit" disabled={sending || !replyText.trim()}
                        className="px-4 py-2.5 rounded-md bg-[#a83a5b] hover:bg-[#bf456b] disabled:opacity-50 text-white inline-flex items-center gap-1.5">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          )
        )}
      </div>

      <ListingForm open={showForm} onClose={() => setShowForm(false)}
        initial={editTarget} mode={editTarget ? 'edit' : 'create'}
        onSuccess={refresh} />
    </div>
  );
};

export default ProfilePage;
